/**
 * 採用情報ページ テキストスライドモジュール
 * 
 * @package LIVMO
 * @version 1.0.0
 */

class RecruitTextSlide {
  constructor() {
    this.slider = document.querySelector('.recruit-header__textSlide');
    this.swiper = null;
    this.wrapper = null;
    this.scrollPosition = 0;
    this.baseScrollSpeed = 1.0; // ベーススピード（ピクセル/フレーム、0.5から1.0に増加）
    this.targetSpeed = 1.0; // 目標スピード（スクロールに応じて設定）
    this.currentSpeed = 1.0; // 現在のスピード（イージングで滑らかに変化）
    this.animationFrameId = null;
    this.isScrolling = false;
    
    // スクロール速度計算用
    this.lastScrollY = window.scrollY;
    this.lastScrollTime = Date.now();
    this.scrollVelocity = 0;
    this.maxScrollSpeed = 5.0; // 最大スピード倍率（3.0から5.0に増加）
    this.scrollVelocityThreshold = 50; // スクロール速度の基準値（px/s、100から50に変更してより敏感に）
    this.scrollDirection = 1; // 1: 下にスクロール（右に動く）、-1: 上にスクロール（左に動く）
    
    // イージング設定
    this.easingFactor = 0.08; // イージング係数（0-1、小さいほど滑らか）
    
    if (this.slider) {
      this.init();
    }
  }
  
  /**
   * 初期化
   */
  async init() {
    if (!this.slider) {
      return;
    }
    
    // Swiperが既に読み込まれているか確認、なければ読み込む
    if (typeof Swiper === 'undefined') {
      await this.loadSwiper();
    }
    
    this.initSwiper();
  }
  
  /**
   * Swiperライブラリの動的読み込み
   */
  async loadSwiper() {
    return new Promise((resolve, reject) => {
      if (typeof Swiper !== 'undefined') {
        resolve();
        return;
      }
      
      // CDNからSwiperを読み込み（CSSもロード完了を待機）
      const swiperScript = document.createElement('script');
      swiperScript.src = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';
      swiperScript.async = true;
      
      const swiperCSS = document.createElement('link');
      swiperCSS.rel = 'stylesheet';
      swiperCSS.href = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';
      
      const cssPromise = new Promise((res) => {
        swiperCSS.onload = () => res();
        swiperCSS.onerror = () => res(); // CSS失敗でも継続
      });
      document.head.appendChild(swiperCSS);
      
      swiperScript.onload = () => {
        resolve();
      };
      swiperScript.onerror = () => {
        reject();
      };
      document.head.appendChild(swiperScript);
    });
  }
  
  /**
   * Swiper初期化
   */
  initSwiper() {
    if (typeof Swiper === 'undefined') {
      return;
    }
    
    if (!this.slider) {
      return;
    }
    
    
    // wrapperを直接取得（Swiper初期化前でも取得可能）
    const wrapper = this.slider.querySelector('.swiper-wrapper');
    if (!wrapper) {
      return;
    }
    this.wrapper = wrapper;
    
    const config = {
      slidesPerView: 'auto',
      spaceBetween: 100,
      loop: false, // loopを無効にして手動制御
      speed: 0, // アニメーション速度は0にして、手動で制御
      allowTouchMove: false,
      freeMode: {
        enabled: false,
      },
      on: {
        init: () => {
          // 少し遅延させてから連続スクロールを開始
          setTimeout(() => {
            this.startContinuousScroll();
          }, 200);
        },
      }
    };

    this.swiper = new Swiper(this.slider, config);
    
    // スクロールイベントを監視して速度を調整
    this.initScrollSpeedControl();
  }
  
  /**
   * スクロール速度制御の初期化
   */
  initScrollSpeedControl() {
    let ticking = false;
    
    const updateScrollSpeed = () => {
      // Lenisが利用可能な場合はLenisから取得、そうでなければ通常の方法
      const currentScrollY = (window.lenis && window.lenis.isInitialized) 
        ? window.lenis.scroll 
        : window.scrollY;
      const currentTime = Date.now();
      const timeDelta = currentTime - this.lastScrollTime;
      
      if (timeDelta > 0) {
        // スクロール方向を検知
        const scrollDeltaY = currentScrollY - this.lastScrollY;
        
        // スクロール方向を更新（下にスクロール = 1、上にスクロール = -1）
        if (Math.abs(scrollDeltaY) > 1) {
          this.scrollDirection = scrollDeltaY > 0 ? 1 : -1;
        }
        
        // スクロール速度を計算（ピクセル/秒）
        const scrollDelta = Math.abs(scrollDeltaY);
        const scrollVelocity = scrollDelta / (timeDelta / 1000); // ピクセル/秒
        
        // スクロール速度に応じてカルーセルの速度を調整
        // スクロールが速いほど、カルーセルも速く動く
        // 速度範囲: 0.5 (停止時) ～ 5.0 (高速スクロール時)
        const normalizedVelocity = Math.min(scrollVelocity / this.scrollVelocityThreshold, 1); // 0-1に正規化（より敏感に反応）
        // より大きな速度変化を実現するため、指数関数的に増加
        const easedVelocity = normalizedVelocity * normalizedVelocity; // 2乗することで、より大きな変化を実現
        const speedMultiplier = 1 + easedVelocity * (this.maxScrollSpeed - 1);
        
        // 目標速度を設定（実際の速度はイージングで滑らかに変化）
        if (scrollVelocity < 10) {
          this.targetSpeed = this.baseScrollSpeed * this.scrollDirection;
        } else {
          // スクロール方向に応じて速度の符号を設定
          this.targetSpeed = this.baseScrollSpeed * speedMultiplier * this.scrollDirection;
        }
        
        this.lastScrollY = currentScrollY;
        this.lastScrollTime = currentTime;
      }
      
      ticking = false;
    };
    
    // スクロールイベントリスナー（throttle処理）
    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(updateScrollSpeed);
        ticking = true;
      }
    }, { passive: true });
    
    // 定期的にスクロール速度を更新（スクロール停止時の減速処理）
    setInterval(() => {
      const timeSinceLastScroll = Date.now() - this.lastScrollTime;
      if (timeSinceLastScroll > 100) {
        // スクロールが停止している場合は、徐々にベーススピードに戻す（方向は維持）
        this.targetSpeed = this.baseScrollSpeed * this.scrollDirection;
      }
    }, 50);
  }
  
  /**
   * 連続スクロールを開始
   */
  startContinuousScroll() {
    
    if (!this.wrapper) {
      return;
    }
    
    // スライド要素を直接取得
    const slides = this.wrapper.querySelectorAll('.swiper-slide');
    
    if (!slides || slides.length === 0) {
      return;
    }
    
    // スライドの総幅を計算
    let totalWidth = 0;
    slides.forEach((slide, index) => {
      const width = slide.offsetWidth || slide.getBoundingClientRect().width;
      totalWidth += width + 100; // spaceBetweenを考慮
    });
    
    
    if (totalWidth === 0) {
      return;
    }
    
    // スライドを複製して無限ループを実現
    slides.forEach(slide => {
      const clone = slide.cloneNode(true);
      this.wrapper.appendChild(clone);
    });
    
    this.isScrolling = true;
    this.scrollPosition = 0;
    
    
    // requestAnimationFrameで滑らかに連続スクロール
    const animate = () => {
      if (!this.isScrolling || !this.wrapper) {
        return;
      }
      
      // イージングで現在速度を目標速度に向かって滑らかに変化させる
      // easeOutCubic風のイージング（線形補間）
      const speedDiff = this.targetSpeed - this.currentSpeed;
      this.currentSpeed += speedDiff * this.easingFactor;
      
      // 速度が非常に小さい場合は目標速度に直接設定（微細な振動を防ぐ）
      if (Math.abs(speedDiff) < 0.01) {
        this.currentSpeed = this.targetSpeed;
      }
      
      // スクロール位置を更新（currentSpeedの符号に応じて方向が変わる）
      this.scrollPosition -= this.currentSpeed;
      
      // ループ処理：右に動く場合（正の方向、scrollPositionは負の値になる）
      if (this.currentSpeed > 0 && totalWidth > 0 && Math.abs(this.scrollPosition) >= totalWidth) {
        this.scrollPosition = 0;
      }
      // ループ処理：左に動く場合（負の方向、scrollPositionは正の値に近づく）
      if (this.currentSpeed < 0 && totalWidth > 0) {
        // 左に動く場合、scrollPositionが0以上になったらリセット
        if (this.scrollPosition >= 0) {
          this.scrollPosition = -totalWidth;
        }
        // または、scrollPositionが-totalWidthより小さくなった場合もリセット
        if (this.scrollPosition < -totalWidth) {
          this.scrollPosition = 0;
        }
      }
      
      // transformを直接適用
      this.wrapper.style.transform = `translate3d(${this.scrollPosition}px, 0, 0)`;
      this.wrapper.style.transition = 'none';
      
      // 次のフレームで継続
      this.animationFrameId = requestAnimationFrame(animate);
    };
    
    // アニメーション開始
    this.animationFrameId = requestAnimationFrame(animate);
  }
  
  /**
   * スクロールを停止
   */
  stopContinuousScroll() {
    this.isScrolling = false;
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }
  
  /**
   * 破棄
   */
  destroy() {
    this.stopContinuousScroll();
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    this.wrapper = null;
  }
}

// DOMContentLoadedで初期化
function bootRecruitTextSlide() {
  if (document.querySelector('.recruit-header__textSlide')) {
    if (!window.__recruitTextSlideInstance) {
      window.__recruitTextSlideInstance = new RecruitTextSlide();
    }
  }
}

document.addEventListener('DOMContentLoaded', bootRecruitTextSlide);

export default RecruitTextSlide;

